import type { IObservableArray } from 'mobx';
import { when } from 'mobx';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import {
  type Campaign,
  type OmniCampaign,
  type Segment,
  TargetableClass,
  type Targeting,
} from '@feathr/blackbox';
import type { ISegmentSelectChangeProps } from '@feathr/extender/components/SegmentSelect';
import SegmentSelect from '@feathr/extender/components/SegmentSelect';
import { useStore } from '@feathr/extender/state';
interface IProps {
  campaign: Campaign;
  targeting: Targeting;
  targetings: IObservableArray<Targeting>;
  onRemove: (targeting: Targeting) => void;
  isReconcilingEmails?: boolean;
  showFiltersInModal?: boolean;
}

function SegmentTargeting({
  campaign,
  targeting,
  targetings,
  onRemove,
  isReconcilingEmails,
  showFiltersInModal = false,
}: Readonly<IProps>): JSX.Element {
  const { Segments } = useStore();
  const segmentId = targeting.get('target_data');
  const { isEmailMappingCampaign, isFacebook, isOmniCampaign } = campaign;
  const { t } = useTranslation();

  async function handleChangeSegment({ included, id }: ISegmentSelectChangeProps): Promise<void> {
    if (id) {
      const newSegment = Segments.get(id);
      await when(() => !newSegment.isPending);
      targeting.set({ included, name: newSegment.get('name'), target_data: id });
    }
  }

  function handleRemoveSegment(): void {
    onRemove(targeting);
  }

  function handleCreateSegment(newSegment: Segment): void {
    const groupName = t('Group created for {{- campaignName }}', { campaignName: campaign.name });
    newSegment.set({
      name: groupName,
      predicates: [],
      account: campaign.get('account'),
      type: TargetableClass.segment,
    });
  }

  const isMetaRetargetingCampaign = isFacebook && !isEmailMappingCampaign;
  const isMetaEmailMappingCampaign = isFacebook && isEmailMappingCampaign;

  const facebookFilter = isFacebook ? { predicates__elemMatch: { attr_against: 'loc_url' } } : {};

  let stat = isEmailMappingCampaign ? 'num_emails_total' : 'num_cookies_total';
  if (isOmniCampaign) {
    const isMetaChannelOnly = (campaign as OmniCampaign).isMetaChannelOnly;
    stat = isMetaChannelOnly ? 'num_users_reachable_meta' : 'num_users_reachable';
  }

  return (
    <SegmentSelect
      campaign={campaign}
      disabled={isReconcilingEmails}
      excludeIds={targetings
        .filter((t) => t.get('target_data') || false)
        .map((t) => t.get('target_data')!)}
      filters={isMetaRetargetingCampaign ? facebookFilter : {}}
      hideIncluded={isMetaEmailMappingCampaign}
      included={targeting.get('included')}
      key={segmentId || 'add'}
      name={'segment_select'}
      onChange={handleChangeSegment}
      onCreate={handleCreateSegment}
      onRemove={handleRemoveSegment}
      showFiltersInModal={showFiltersInModal}
      stat={stat}
      value={segmentId}
    />
  );
}

export default observer(SegmentTargeting);
